/**
 * DeliverablesTab — section "Livrables digitaux" de la page Réglages (S2.5e).
 *
 * Trois zones :
 *
 *   1. Format des fichiers — formats actifs (jpg/pdf), DPI, qualité JPG.
 *   2. Document accompagnant — 2 PDF optionnels (FR + EN) inclus dans le
 *      ZIP de livraison (S6+).
 *   3. Variantes existantes — bouton de batch régénération qui poste tous
 *      les jobs dans la queue S5b. Progression suivie via polling.
 *
 * Tout passe par les primitives UI / forms existantes : `<Field>`,
 * `<Input>`, `<Button>`, `<RadioCard>`, `<Modal>`, `<EmptyState>`,
 * `<Badge>`. Aucune `div` jetable, aucun `<input>` natif en formulaire —
 * verrous CI tenus.
 */

import { useEffect } from 'react'
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Loader2, Trash2, Upload } from 'lucide-react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { z } from 'zod'

import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import EmptyState from '@/components/ui/EmptyState'
import Field from '@/components/ui/Field'
import FilePickerButton from '@/components/ui/FilePickerButton'
import Input from '@/components/ui/Input'
import RadioCard from '@/components/ui/RadioCard'
import { useConfirm } from '@/contexts/confirm-dialog-context'
import {
  useAddShopCarouselImage,
  useDeleteDeliverableDoc,
  useDeleteShopCarouselImage,
  useDeliverableSettings,
  useRegenerateAllDeliverables,
  useReorderShopCarouselImages,
  useUpdateDeliverableConfig,
  useUpdateShopCarouselImageScope,
  useUploadDeliverableDoc,
} from '@/hooks/useDeliverableSettings'
import {
  DELIVERABLE_DOC_LANG_LABEL_FR,
  DELIVERABLE_DPI_LABEL_FR,
  DELIVERABLE_FORMAT_LABEL_FR,
  SHOP_CAROUSEL_SCOPE_HINT_FR,
  SHOP_CAROUSEL_SCOPE_LABEL_FR,
  type DeliverableDocLang,
  type DeliverableDocResponse,
  type DeliverableDpi,
  type DeliverableFormat,
  type ShopCarouselImageResponse,
  type ShopCarouselScope,
} from '@/types/deliverable-settings'

import './DeliverablesTab.css'

// ──────────────────────────────────────────────────────────────────────────────
// Schéma RHF + Zod — borné par les valeurs renvoyées par le BE
// ──────────────────────────────────────────────────────────────────────────────

const FORM_SCHEMA = z.object({
  formats: z.array(z.enum(['jpg', 'pdf'])).min(1, 'Au moins un format doit être actif.'),
  dpi: z.union([z.literal(150), z.literal(250), z.literal(300), z.literal(600)]),
  jpg_quality: z.number().int().min(70).max(100),
})

type FormValues = z.infer<typeof FORM_SCHEMA>

// ──────────────────────────────────────────────────────────────────────────────
// Helpers — agrégation progress
// ──────────────────────────────────────────────────────────────────────────────

interface ProgressSummary {
  inProgress: number // scheduled + running
  done: number
  failed: number
  total: number
  hasActivity: boolean
}

interface ProgressShape {
  scheduled?: number
  running?: number
  done?: number
  failed?: number
  paused?: number
}

function summariseProgress(progress: ProgressShape): ProgressSummary {
  const scheduled = progress.scheduled ?? 0
  const running = progress.running ?? 0
  const done = progress.done ?? 0
  const failed = progress.failed ?? 0
  const paused = progress.paused ?? 0
  const inProgress = scheduled + running
  const total = inProgress + done + failed + paused
  return {
    inProgress,
    done,
    failed,
    total,
    hasActivity: inProgress > 0,
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// Composant principal
// ──────────────────────────────────────────────────────────────────────────────

export default function DeliverablesTab() {
  const { confirm } = useConfirm()

  const settingsQuery = useDeliverableSettings()

  const updateMutation = useUpdateDeliverableConfig()
  const uploadMutation = useUploadDeliverableDoc()
  const deleteDocMutation = useDeleteDeliverableDoc()
  const regenAllMutation = useRegenerateAllDeliverables()
  const addCarouselMutation = useAddShopCarouselImage()
  const deleteCarouselMutation = useDeleteShopCarouselImage()
  const updateCarouselScopeMutation = useUpdateShopCarouselImageScope()
  const reorderCarouselMutation = useReorderShopCarouselImages()

  const data = settingsQuery.data

  const formMethods = useForm<FormValues>({
    resolver: zodResolver(FORM_SCHEMA),
    defaultValues: {
      formats: ['jpg', 'pdf'],
      dpi: 300,
      jpg_quality: 100,
    },
  })

  const { handleSubmit, register, control, setValue, reset, formState } = formMethods

  useEffect(() => {
    if (data) {
      reset({
        formats: data.config.formats,
        dpi: data.config.dpi as DeliverableDpi,
        jpg_quality: data.config.jpg_quality,
      })
    }
  }, [data, reset])

  const watchedFormats = useWatch({ control, name: 'formats' })
  const watchedDpi = useWatch({ control, name: 'dpi' })
  const watchedQuality = useWatch({ control, name: 'jpg_quality' })
  const jpgActive = watchedFormats.includes('jpg')

  const onSubmit = handleSubmit((values) => {
    updateMutation.mutate(
      {
        formats: values.formats,
        dpi: values.dpi,
        jpg_quality: values.jpg_quality,
      },
      {
        onSuccess: () => {
          toast.success('Réglages livrables enregistrés.')
        },
        onError: (err) => {
          toast.error(err.detail ?? 'Mise à jour impossible.')
        },
      },
    )
  })

  const toggleFormat = (format: DeliverableFormat) => {
    const current = watchedFormats
    const next = current.includes(format)
      ? current.filter((f) => f !== format)
      : [...current, format]
    setValue('formats', next, { shouldDirty: true, shouldValidate: true })
  }

  const dirty = formState.isDirty
  const submitting = updateMutation.isPending

  const handleRegenerateAll = async () => {
    const ok = await confirm({
      title: 'Régénérer tous les livrables ?',
      message:
        'Toutes les variantes actives avec un format digital seront re-générées en arrière-plan avec la config courante. ' +
        'Cela peut prendre plusieurs minutes selon le catalogue. ' +
        "L'opération s'ajoute à la file et n'est pas bloquante.",
      confirmText: 'Régénérer',
      variant: 'default',
    })
    if (!ok) return
    regenAllMutation.mutate(undefined, {
      onSuccess: (res) => {
        if (res.enqueued_count === 0) {
          toast.info(
            'Aucune variante éligible. Crée une variante active avec un format digital pour activer la régénération.',
          )
        } else {
          toast.success(
            `${res.enqueued_count} variante${res.enqueued_count > 1 ? 's' : ''} en file de régénération.`,
          )
          // Polling automatique pris en charge par useDeliverableSettings
          // dès que la prochaine query verra `regenerate_progress` > 0.
        }
      },
      onError: (err) => {
        toast.error(err.detail ?? 'Lancement impossible.')
      },
    })
  }

  if (settingsQuery.isLoading) {
    return (
      <div className="deliverables-loading">
        <Loader2 aria-hidden="true" className="deliverables-spinner" size={20} />
        <span className="ui-text-muted">Chargement des réglages…</span>
      </div>
    )
  }
  if (!data) {
    return (
      <EmptyState
        title="Réglages indisponibles"
        description="Le serveur n'a pas pu remonter la configuration. Réessaie dans quelques secondes."
      />
    )
  }

  const formatsAllowed = data.formats_allowed ?? ['jpg', 'pdf']
  const dpiChoices = data.dpi_choices ?? [150, 250, 300, 600]
  const jpgQualityMin = data.jpg_quality_min ?? 70
  const jpgQualityMax = data.jpg_quality_max ?? 100
  const progress = summariseProgress(data.regenerate_progress)

  return (
    <FormProvider {...formMethods}>
      <section className="settings-section deliverables-tab">
        {/* ──────────────────────────────────────────────────────────────
            1. FORMAT DES FICHIERS — formats / DPI / qualité JPG
           ────────────────────────────────────────────────────────────── */}
        <header className="settings-section-header">
          <div>
            <h2 className="ui-h4 settings-section-title">Format des fichiers</h2>
            <p className="ui-body ui-text-muted settings-section-subtitle">
              Ces réglages sont consommés par toute génération de livrables — les nouvelles
              variantes actives utilisent immédiatement la config courante. Les variantes existantes
              restent intactes jusqu'à régénération (cf. plus bas).
            </p>
          </div>
        </header>

        <form className="deliverables-form" onSubmit={onSubmit} noValidate>
          {/* Formats actifs */}
          <fieldset className="deliverables-fieldset">
            <legend className="deliverables-legend">Formats générés</legend>
            <p className="ui-text-muted deliverables-fieldset-hint">
              Au moins un format actif. Le ZIP de livraison (à venir) inclura tous les formats
              cochés.
            </p>
            <ul className="deliverables-format-list" role="list">
              {formatsAllowed.map((format) => {
                const formatTyped = format as DeliverableFormat
                const active = watchedFormats.includes(formatTyped)
                return (
                  <li key={format}>
                    <RadioCard
                      multi
                      selected={active}
                      onSelect={() => toggleFormat(formatTyped)}
                      label={DELIVERABLE_FORMAT_LABEL_FR[formatTyped]}
                      description={
                        formatTyped === 'jpg'
                          ? 'Image bitmap — parfait pour impression et écran.'
                          : 'Document monopage — pratique côté acheteur pour partager / archiver.'
                      }
                    />
                  </li>
                )
              })}
            </ul>
            {formState.errors.formats && (
              <p className="ui-text-danger deliverables-error" role="alert">
                {formState.errors.formats.message}
              </p>
            )}
          </fieldset>

          {/* DPI */}
          <fieldset className="deliverables-fieldset">
            <legend className="deliverables-legend">Résolution (DPI)</legend>
            <p className="ui-text-muted deliverables-fieldset-hint">
              Plus le DPI est élevé, plus le fichier est lourd et précis à l'impression.{' '}
              <strong>300 DPI</strong> = standard print.
            </p>
            <ul className="deliverables-dpi-list" role="list">
              {dpiChoices.map((dpi) => {
                const dpiTyped = dpi as DeliverableDpi
                return (
                  <li key={dpi}>
                    <RadioCard
                      selected={watchedDpi === dpiTyped}
                      onSelect={() =>
                        setValue('dpi', dpiTyped, {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                      }
                      label={`${dpi} DPI`}
                      description={DELIVERABLE_DPI_LABEL_FR[dpiTyped]}
                    />
                  </li>
                )
              })}
            </ul>
          </fieldset>

          {/* Qualité JPG */}
          <fieldset className="deliverables-fieldset" disabled={!jpgActive}>
            <legend className="deliverables-legend">
              Qualité JPG{' '}
              {!jpgActive && <Badge variant="default">Inactif — JPG non sélectionné</Badge>}
            </legend>
            <p className="ui-text-muted deliverables-fieldset-hint">
              Entre {jpgQualityMin} et {jpgQualityMax}. <strong>100</strong> = quasi-lossless
              (taille A3 ≈ 30-50 Mo). <strong>85</strong> = compromis qualité/poids (≈ 5-8 Mo).
            </p>
            <Field label={`Valeur (${watchedQuality})`}>
              {(controlProps) => (
                <Input
                  type="number"
                  min={jpgQualityMin}
                  max={jpgQualityMax}
                  step={1}
                  {...register('jpg_quality', { valueAsNumber: true })}
                  {...controlProps}
                />
              )}
            </Field>
            {formState.errors.jpg_quality && (
              <p className="ui-text-danger deliverables-error" role="alert">
                {formState.errors.jpg_quality.message}
              </p>
            )}
          </fieldset>

          <div className="deliverables-form-actions">
            <Button type="submit" variant="primary" disabled={!dirty || submitting}>
              {submitting ? 'Enregistrement…' : 'Enregistrer la config'}
            </Button>
            {dirty && !submitting && (
              <Button
                type="button"
                variant="ghost"
                onClick={() =>
                  reset({
                    formats: data.config.formats,
                    dpi: data.config.dpi as DeliverableDpi,
                    jpg_quality: data.config.jpg_quality,
                  })
                }
              >
                Annuler
              </Button>
            )}
          </div>
        </form>

        {/* ──────────────────────────────────────────────────────────────
            2. DOCUMENT ACCOMPAGNANT — 2 PDF (FR + EN) optionnels
           ────────────────────────────────────────────────────────────── */}
        <header className="settings-section-header deliverables-section-divider">
          <div>
            <h2 className="ui-h4 settings-section-title">Document accompagnant</h2>
            <p className="ui-body ui-text-muted settings-section-subtitle">
              PDF optionnels inclus dans le ZIP de livraison aux acheteurs — certificat
              d'authenticité, instructions, mots de remerciement. Maximum 10 Mo par PDF.
            </p>
          </div>
        </header>

        <ul className="deliverables-doc-list" role="list">
          {(['fr', 'en'] as const).map((lang) => (
            <li key={lang}>
              <DeliverableDocCard
                lang={lang}
                doc={lang === 'fr' ? data.document_fr : data.document_en}
                onUpload={(file) => {
                  uploadMutation.mutate(
                    { lang, file },
                    {
                      onSuccess: (res) => {
                        toast.success(
                          `« ${res.original_filename} » chargé en ${DELIVERABLE_DOC_LANG_LABEL_FR[lang]}.`,
                        )
                      },
                      onError: (err) => {
                        toast.error(err.detail ?? 'Upload impossible.')
                      },
                    },
                  )
                }}
                onDelete={async () => {
                  const ok = await confirm({
                    title: 'Supprimer ce document ?',
                    message: `Le PDF accompagnant ${DELIVERABLE_DOC_LANG_LABEL_FR[lang].toLowerCase()} ne sera plus inclus dans le ZIP de livraison.`,
                    confirmText: 'Supprimer',
                    variant: 'danger',
                  })
                  if (!ok) return
                  deleteDocMutation.mutate(lang, {
                    onSuccess: () => {
                      toast.success(
                        `Document ${DELIVERABLE_DOC_LANG_LABEL_FR[lang].toLowerCase()} supprimé.`,
                      )
                    },
                    onError: (err) => {
                      toast.error(err.detail ?? 'Suppression impossible.')
                    },
                  })
                }}
                isUploading={uploadMutation.isPending && uploadMutation.variables?.lang === lang}
                isDeleting={deleteDocMutation.isPending && deleteDocMutation.variables === lang}
              />
            </li>
          ))}
        </ul>

        {/* ──────────────────────────────────────────────────────────────
            3. IMAGES DE CARROUSEL BOUTIQUE — visuels boutique-wide (multi)
           ────────────────────────────────────────────────────────────── */}
        <header className="settings-section-header deliverables-section-divider">
          <div>
            <h2 className="ui-h4 settings-section-title">Images de carrousel boutique</h2>
            <p className="ui-body ui-text-muted settings-section-subtitle">
              Visuels statiques boutique-wide ajoutés en fin de carrousel de chaque listing publié,
              dans l'ordre d'ajout (chart de ratios, politique de livraison, certificat…). Chaque
              image peut cibler <strong>tous les articles</strong>, uniquement{' '}
              <strong>les articles digitaux</strong> ou uniquement{' '}
              <strong>les articles physiques</strong> — le bon visuel est servi automatiquement au
              moment du publish. Maximum 5&nbsp;Mo par fichier, JPG / PNG / WebP, ≥&nbsp;800&nbsp;px
              côté court.
              <br />
              <strong>Cap Etsy</strong>&nbsp;: 10 images par annonce au total (1 œuvre + jusqu'à 8
              mockups + annexes restantes). Les listings déjà publiés ne sont pas modifiés — seules
              les nouvelles publications utilisent la liste courante.
            </p>
          </div>
        </header>

        <ShopCarouselImagesCard
          images={data.shop_carousel_images ?? []}
          onAdd={(file) => {
            addCarouselMutation.mutate(
              { file, scope: 'both' },
              {
                onSuccess: (res) => {
                  toast.success(`« ${res.original_filename} » ajoutée.`)
                },
                onError: (err) => {
                  toast.error(err.detail ?? 'Ajout impossible.')
                },
              },
            )
          }}
          onChangeScope={(image, scope) => {
            if (image.scope === scope) return
            updateCarouselScopeMutation.mutate(
              { imageId: image.id, scope },
              {
                onError: (err) => {
                  toast.error(err.detail ?? 'Mise à jour impossible.')
                },
              },
            )
          }}
          onDelete={async (image) => {
            const ok = await confirm({
              title: 'Supprimer cette image ?',
              message:
                `« ${image.original_filename} » ne sera plus ajoutée aux nouveaux listings ` +
                'boutique. Les publications existantes gardent leur version.',
              confirmText: 'Supprimer',
              variant: 'danger',
            })
            if (!ok) return
            deleteCarouselMutation.mutate(image.id, {
              onSuccess: () => {
                toast.success('Image supprimée.')
              },
              onError: (err) => {
                toast.error(err.detail ?? 'Suppression impossible.')
              },
            })
          }}
          onReorder={(orderedIds) => {
            reorderCarouselMutation.mutate(orderedIds, {
              onError: (err) => {
                toast.error(err.detail ?? 'Réordre impossible.')
              },
            })
          }}
          isAdding={addCarouselMutation.isPending}
          isDeleting={deleteCarouselMutation.isPending}
          isReordering={reorderCarouselMutation.isPending}
          pendingScopeUpdateId={
            updateCarouselScopeMutation.isPending
              ? (updateCarouselScopeMutation.variables?.imageId ?? null)
              : null
          }
        />

        {/* ──────────────────────────────────────────────────────────────
            4. VARIANTES EXISTANTES — batch queue S5b
           ────────────────────────────────────────────────────────────── */}
        <header className="settings-section-header deliverables-section-divider">
          <div>
            <h2 className="ui-h4 settings-section-title">Variantes existantes</h2>
            <p className="ui-body ui-text-muted settings-section-subtitle">
              Quand tu changes la config (formats, DPI, qualité), les variantes existantes gardent
              leurs livrables jusqu'à régénération. Le bouton ci-dessous enqueue tous les jobs dans
              la file — traitement en arrière-plan, sans bloquer le portail.
            </p>
          </div>
          <Button
            variant="primary"
            onClick={handleRegenerateAll}
            disabled={regenAllMutation.isPending || progress.hasActivity}
          >
            {regenAllMutation.isPending ? 'Lancement…' : 'Régénérer toutes les variantes'}
          </Button>
        </header>

        <ProgressPanel summary={progress} />
      </section>
    </FormProvider>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Card document accompagnant — upload / preview / delete
// ──────────────────────────────────────────────────────────────────────────────

interface DeliverableDocCardProps {
  lang: DeliverableDocLang
  doc: DeliverableDocResponse | null | undefined
  onUpload: (file: File) => void
  onDelete: () => void
  isUploading: boolean
  isDeleting: boolean
}

function DeliverableDocCard({
  lang,
  doc,
  onUpload,
  onDelete,
  isUploading,
  isDeleting,
}: DeliverableDocCardProps) {
  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} o`
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} ko`
    return `${(bytes / 1024 / 1024).toFixed(1)} Mo`
  }

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString('fr-FR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return iso
    }
  }

  return (
    <article className="deliverables-doc-card">
      <header className="deliverables-doc-card-header">
        <h3 className="ui-h6 deliverables-doc-title">{DELIVERABLE_DOC_LANG_LABEL_FR[lang]}</h3>
        {doc ? (
          <Badge variant="success">Posé</Badge>
        ) : (
          <Badge variant="default">Aucun document</Badge>
        )}
      </header>

      {doc ? (
        <div className="deliverables-doc-card-body">
          <p className="ui-body deliverables-doc-filename">{doc.original_filename}</p>
          <p className="ui-text-muted deliverables-doc-meta">
            {formatBytes(doc.bytes)} · ajouté {formatDate(doc.uploaded_at)}
          </p>
          <div className="deliverables-doc-card-actions">
            <a
              className="ui-button ui-button--ghost ui-button--sm"
              href={doc.download_url}
              target="_blank"
              rel="noreferrer"
            >
              Aperçu
            </a>
            <FilePickerButton
              accept="application/pdf,.pdf"
              variant="ghost"
              size="sm"
              disabled={isUploading}
              onPick={onUpload}
            >
              <Upload aria-hidden="true" size={14} />
              Remplacer
            </FilePickerButton>
            <Button
              type="button"
              variant="danger"
              size="sm"
              onClick={onDelete}
              disabled={isDeleting}
            >
              <Trash2 aria-hidden="true" size={14} />
              Supprimer
            </Button>
          </div>
        </div>
      ) : (
        <div className="deliverables-doc-card-body">
          <p className="ui-text-muted">
            Pas encore de document accompagnant en{' '}
            {DELIVERABLE_DOC_LANG_LABEL_FR[lang].toLowerCase()}.
          </p>
          <FilePickerButton
            accept="application/pdf,.pdf"
            variant="primary"
            size="sm"
            disabled={isUploading}
            onPick={onUpload}
          >
            <Upload aria-hidden="true" size={14} />
            {isUploading ? 'Upload…' : 'Choisir un PDF'}
          </FilePickerButton>
        </div>
      )}
    </article>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Card images carrousel boutique — liste multi + add (scope) / delete par entrée
// ──────────────────────────────────────────────────────────────────────────────

interface ShopCarouselImagesCardProps {
  images: ShopCarouselImageResponse[]
  onAdd: (file: File) => void
  onChangeScope: (image: ShopCarouselImageResponse, scope: ShopCarouselScope) => void
  onDelete: (image: ShopCarouselImageResponse) => void
  onReorder: (orderedIds: string[]) => void
  isAdding: boolean
  isDeleting: boolean
  isReordering: boolean
  pendingScopeUpdateId: string | null
}

const SHOP_CAROUSEL_SCOPE_ORDER: readonly ShopCarouselScope[] = [
  'both',
  'digital',
  'physical',
] as const

function ShopCarouselImagesCard({
  images,
  onAdd,
  onChangeScope,
  onDelete,
  onReorder,
  isAdding,
  isDeleting,
  isReordering,
  pendingScopeUpdateId,
}: ShopCarouselImagesCardProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = images.findIndex((img) => img.id === active.id)
    const newIndex = images.findIndex((img) => img.id === over.id)
    if (oldIndex < 0 || newIndex < 0) return
    const nextOrder = arrayMove(images, oldIndex, newIndex).map((img) => img.id)
    onReorder(nextOrder)
  }

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} o`
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} ko`
    return `${(bytes / 1024 / 1024).toFixed(1)} Mo`
  }

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString('fr-FR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return iso
    }
  }

  return (
    <article className="deliverables-ratios-card">
      <header className="deliverables-ratios-card-header">
        <h3 className="ui-h6 deliverables-ratios-title">Visuels injectés ({images.length})</h3>
        <FilePickerButton
          accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
          variant="primary"
          size="sm"
          disabled={isAdding}
          onPick={(file) => onAdd(file)}
        >
          <Upload aria-hidden="true" size={14} />
          {isAdding ? 'Ajout…' : 'Ajouter une image'}
        </FilePickerButton>
      </header>

      <p className="ui-text-muted deliverables-fieldset-hint">
        Chaque image est ajoutée par défaut sur <strong>tous les articles</strong>. Tu peux
        re-cibler à tout moment via le sélecteur de chaque entrée — pas besoin de re-uploader.
      </p>

      {images.length === 0 ? (
        <p className="ui-text-muted">
          Aucune image n'est encore configurée. Sans image, les nouveaux listings seront publiés
          sans visuels d'information additionnels.
        </p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={images.map((img) => img.id)}
            strategy={verticalListSortingStrategy}
          >
            <ul className="deliverables-carousel-list">
              {images.map((image, idx) => (
                <SortableCarouselItem
                  key={image.id}
                  image={image}
                  rank={idx + 1}
                  dragDisabled={isReordering || images.length < 2}
                  onChangeScope={onChangeScope}
                  onDelete={onDelete}
                  isDeleting={isDeleting}
                  pendingScopeUpdateId={pendingScopeUpdateId}
                  formatBytes={formatBytes}
                  formatDate={formatDate}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}
    </article>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Item carrousel boutique — sortable (handle drag + métadonnées + actions)
// ──────────────────────────────────────────────────────────────────────────────

interface SortableCarouselItemProps {
  image: ShopCarouselImageResponse
  rank: number
  dragDisabled: boolean
  onChangeScope: (image: ShopCarouselImageResponse, scope: ShopCarouselScope) => void
  onDelete: (image: ShopCarouselImageResponse) => void
  isDeleting: boolean
  pendingScopeUpdateId: string | null
  formatBytes: (bytes: number) => string
  formatDate: (iso: string) => string
}

function SortableCarouselItem({
  image,
  rank,
  dragDisabled,
  onChangeScope,
  onDelete,
  isDeleting,
  pendingScopeUpdateId,
  formatBytes,
  formatDate,
}: SortableCarouselItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: image.id,
    disabled: dragDisabled,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <li ref={setNodeRef} style={style} className="deliverables-carousel-item">
      <span
        className="deliverables-carousel-handle"
        {...attributes}
        {...listeners}
        aria-label={`Réordonner « ${image.original_filename} »`}
      >
        <GripVertical aria-hidden="true" size={16} />
      </span>
      <span className="deliverables-carousel-rank">#{rank}</span>
      <div className="deliverables-carousel-meta">
        <p className="ui-body deliverables-ratios-filename">{image.original_filename}</p>
        <p className="ui-text-muted deliverables-ratios-meta">
          {image.width_px}×{image.height_px}px · {formatBytes(image.bytes)} · ajoutée{' '}
          {formatDate(image.uploaded_at)}
        </p>
        <ShopCarouselScopeToggle
          imageId={image.id}
          value={image.scope}
          onChange={(scope) => onChangeScope(image, scope)}
          disabled={pendingScopeUpdateId === image.id}
        />
      </div>
      <div className="deliverables-ratios-actions">
        <a
          className="ui-button ui-button--ghost ui-button--sm"
          href={image.download_url}
          target="_blank"
          rel="noreferrer"
        >
          Aperçu
        </a>
        <Button
          type="button"
          variant="danger"
          size="sm"
          onClick={() => onDelete(image)}
          disabled={isDeleting}
        >
          <Trash2 aria-hidden="true" size={14} />
          Supprimer
        </Button>
      </div>
    </li>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Toggle 3-positions inline — cible d'une image carrousel
// ──────────────────────────────────────────────────────────────────────────────

interface ShopCarouselScopeToggleProps {
  imageId: string
  value: ShopCarouselScope
  onChange: (scope: ShopCarouselScope) => void
  disabled: boolean
}

function ShopCarouselScopeToggle({
  imageId,
  value,
  onChange,
  disabled,
}: ShopCarouselScopeToggleProps) {
  const groupId = `shop-carousel-scope-${imageId}`
  return (
    <div
      role="radiogroup"
      aria-label="Cible du visuel"
      className="deliverables-carousel-scope-toggle"
      id={groupId}
    >
      {SHOP_CAROUSEL_SCOPE_ORDER.map((scope) => {
        const selected = value === scope
        return (
          <Button
            key={scope}
            type="button"
            role="radio"
            aria-checked={selected}
            variant={selected ? 'primary' : 'ghost'}
            size="sm"
            disabled={disabled}
            onClick={() => onChange(scope)}
            title={SHOP_CAROUSEL_SCOPE_HINT_FR[scope]}
          >
            {SHOP_CAROUSEL_SCOPE_LABEL_FR[scope]}
          </Button>
        )
      })}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Panel progression — KPIs simples
// ──────────────────────────────────────────────────────────────────────────────

function ProgressPanel({ summary }: { summary: ProgressSummary }) {
  if (summary.total === 0) {
    return (
      <EmptyState
        title="Aucun batch en cours"
        description="Lance « Régénérer toutes les variantes » quand tu veux propager une nouvelle config aux livrables existants."
      />
    )
  }

  return (
    <div className="deliverables-progress" role="status" aria-live="polite">
      <ProgressBlock
        label="En attente / en cours"
        value={summary.inProgress}
        tone={summary.inProgress > 0 ? 'accent' : 'neutral'}
      />
      <ProgressBlock label="Terminés" value={summary.done} tone="success" />
      <ProgressBlock
        label="Échecs"
        value={summary.failed}
        tone={summary.failed > 0 ? 'danger' : 'neutral'}
      />
      <ProgressBlock label="Total" value={summary.total} tone="neutral" />
    </div>
  )
}

interface ProgressBlockProps {
  label: string
  value: number
  tone: 'accent' | 'success' | 'danger' | 'neutral'
}

function ProgressBlock({ label, value, tone }: ProgressBlockProps) {
  const classes = ['deliverables-progress-block', `deliverables-progress-block--${tone}`].join(' ')
  return (
    <div className={classes}>
      <span className="deliverables-progress-value">{value}</span>
      <span className="deliverables-progress-label ui-text-muted">{label}</span>
    </div>
  )
}
